/**
 * PackageStructureViewer - Readonly display of package structure (C1, C2)
 *
 * Displays sets as cards with product names, selectionRule badges,
 * isDefault badges with tooltips, and featureValues as key-value pairs.
 *
 * @see docs/specifications/2026-03-20-package-structure-visual-editor.md (C1, C2)
 */
import React from "react";
import type {
  EnrichedPackageStructure,
  EnrichedPackageStructureSet,
  EnrichedPackageStructureProduct,
} from "../../../../types/adminBilling";
import { SELECTION_RULE_LABELS } from "./constants";

interface PackageStructureViewerProps {
  packageStructure: EnrichedPackageStructure;
}

/** Renders a single product row within a set */
const ProductRow: React.FC<{ product: EnrichedPackageStructureProduct }> = ({
  product,
}) => {
  const displayName = product.productName ?? `(nieznany produkt) ${product.productId}`;
  const featureEntries = product.featureValues
    ? Object.entries(product.featureValues)
    : [];

  return (
    <div className="bg-white rounded border border-neutral-200 p-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-neutral-900">
          {displayName}
        </span>
        {product.isDefault && (
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700"
            title="Produkt domyślny przy tworzeniu subskrypcji"
          >
            Domyślny
          </span>
        )}
      </div>
      {featureEntries.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
          {featureEntries.map(([key, value]) => (
            <span key={key} className="text-xs text-neutral-600">
              <span className="font-mono text-neutral-500">{key}</span>
              {": "}
              <span className="font-medium">{String(value)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/** Renders a single set card */
const SetCard: React.FC<{ set: EnrichedPackageStructureSet; index: number }> = ({
  set,
  index,
}) => {
  const ruleLabel =
    SELECTION_RULE_LABELS[set.selectionRule] ?? set.selectionRule;

  return (
    <div className="bg-neutral-50 rounded-md border border-neutral-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h5 className="text-sm font-semibold text-neutral-900">
          {set.name || `Set ${index + 1}`}
        </h5>
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
          {ruleLabel}
        </span>
      </div>
      {set.products.length > 0 ? (
        <div className="space-y-2">
          {set.products.map((product, pIndex) => (
            <ProductRow
              key={product.productId || pIndex}
              product={product}
            />
          ))}
        </div>
      ) : (
        <p className="text-xs text-neutral-400 italic">
          Brak produktów w tym secie.
        </p>
      )}
    </div>
  );
};

export const PackageStructureViewer: React.FC<PackageStructureViewerProps> = ({
  packageStructure,
}) => {
  if (!packageStructure.sets || packageStructure.sets.length === 0) {
    return (
      <p className="text-sm text-neutral-400 italic">
        Pusta struktura pakietu.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {packageStructure.sets.map((set, index) => (
        <SetCard key={index} set={set} index={index} />
      ))}
    </div>
  );
};
